<template>
  <div class="w-full h-full flex justify-center items-center">
    <div 
      v-if="isSubscribed" 
      ref="chart" 
      class="chart-container"
      :style="{ 
        backgroundColor, 
        width: `${isMobile ? Math.min(width, window.innerWidth - 40) : width}px`, 
        height: `${isMobile ? Math.min(height, window.innerHeight - 200) : height}px` 
      }"
    ></div>
    
    <!-- Carousel for non-subscribed users -->
    <el-carousel 
      v-else 
      :interval="5000" 
      arrow="always" 
      class="w-full h-full bg-white"
      :height="height + 'px'"
    >
      <el-carousel-item v-for="(image, index) in carouselImages" :key="index">
        <div class="w-full h-full">
          <img :src="require(`@/assets/${image}`)" :alt="image" class="w-full h-full object-cover" loading="lazy">
        </div>
      </el-carousel-item>
    </el-carousel>
  </div>
</template>

<script>
import * as echarts from 'echarts';
import 'echarts-wordcloud';
import { ElCarousel, ElCarouselItem, ElNotification } from 'element-plus';
import { debounce } from 'lodash-es';
import html2canvas from 'html2canvas';
import FontFaceObserver from 'fontfaceobserver';

export default {
  name: 'WordCloudChart',
  components: {
    ElCarousel,
    ElCarouselItem
  },
  props: {
    isSubscribed: {
      type: Boolean,
      default: false
    },
    isMobile: {
      type: Boolean,
      default: false
    },
    chartData: {
      type: Array,
      default: () => []
    },
    maskSrc: {
      type: String,
      default: ''
    },
    font: {
      type: String,
      default: 'Roboto'
    },
    backgroundColor: {
      type: String,
      default: '#FFFFFF'
    },
    width: {
      type: Number,
      default: 500
    },
    height: {
      type: Number,
      default: 500
    },
    minSize: {
      type: Number,
      default: 10
    },
    maxSize: {
      type: Number,
      default: 180
    },
    carouselImages: {
      type: Array,
      default: () => []
    },
    window: {
      type: Object,
      default: () => window
    }
  },
  data() {
    return {
      chartInstance: null,
      debouncedUpdate: null
    }
  },
  mounted() {
    this.debouncedUpdate = debounce(this.updateChart, 500);
    
    if (this.isSubscribed) {
      this.initChart();
    }
  },
  watch: {
    chartData: {
      deep: true,
      handler() { this.debouncedUpdate(); }
    },
    maskSrc() { this.debouncedUpdate(); },
    font() { this.debouncedUpdate(); },
    backgroundColor() { 
      // Background color is style-bound, but if we want to redraw chart logic...
      // ECharts instance might need resize if container size changed, but color is CSS.
      // However, export uses this prop.
      // Nothing strictly needed for ECharts UNLESS transparent background support requires config.
      // ECharts by default is transparent. Container has background.
      // So no updateChart needed for color change purely.
    },
    width() { this.debouncedUpdate(); },
    height() { this.debouncedUpdate(); },
    minSize() { this.debouncedUpdate(); },
    maxSize() { this.debouncedUpdate(); },
    isSubscribed(val) {
      if (val) {
        this.$nextTick(this.initChart);
      } else {
        if (this.chartInstance) {
          this.chartInstance.dispose();
          this.chartInstance = null;
        }
      }
    }
  },
  beforeUnmount() {
    if (this.debouncedUpdate) this.debouncedUpdate.cancel();
    if (this.chartInstance) {
      this.chartInstance.dispose();
    }
  },
  methods: {
    resize() {
      if (this.chartInstance) {
        this.chartInstance.resize();
      }
    },
    generateChartOption(maskImage = null) {
      const option = {
        series: [{
          type: 'wordCloud',
          sizeRange: [this.minSize, this.maxSize], 
          rotationRange: [-90, 90],
          gridSize: 1,
          drawOutOfBound: false,
          shrinkToFit: true,
          keepAspect: false,
          layoutAnimation: true,
          width: '100%',
          height: '100%',
          textStyle: {
            fontFamily: `'${this.font}'`,
            color: () => {
              return 'rgb(' + [
                Math.round(Math.random() * 160),
                Math.round(Math.random() * 160), 
                Math.round(Math.random() * 160)
              ].join(',') + ')';
            }
          },
          data: this.chartData
        }]
      };

      if (maskImage) {
        option.series[0].maskImage = maskImage;
      }

      return option;
    },
    initChart() {
      if (!this.$refs.chart) return;
      
      const canvas = document.createElement('canvas');
      canvas.getContext('2d', { willReadFrequently: true });
      
      this.chartInstance = echarts.init(this.$refs.chart, null, {
        renderer: 'canvas'
      });

      this.updateChart();
    },
    async updateChart() {
      if (!this.isSubscribed || !this.$refs.chart) return;
      if (!this.chartInstance) this.initChart();

      try {
        // Wait for font to load
        if (this.font !== 'sans-serif' && this.font !== 'serif') {
           const fontObserver = new FontFaceObserver(this.font);
           try {
             await fontObserver.load(null, 5000); // 5s timeout
           } catch (e) {
             console.warn(`Font ${this.font} failed to load or timed out. Rendering anyway.`);
           }
        }

        const handleMaskImage = (maskImage) => {
          const option = this.generateChartOption(maskImage);
          this.chartInstance.setOption(option);
          this.chartInstance.resize();
        };

        const maskImage = new Image();
        maskImage.crossOrigin = 'Anonymous'; 
        maskImage.onload = () => handleMaskImage(maskImage);
        maskImage.onerror = () => {
          console.warn('Failed to load mask image, using default options (no mask)');
          const option = this.generateChartOption(); 
          this.chartInstance.setOption(option);
          this.chartInstance.resize();
        };
        
        if (this.maskSrc) {
            maskImage.src = this.maskSrc;
        } else {
             handleMaskImage(null);
        }

      } catch (error) {
        console.error('Error updating chart:', error);
        ElNotification({
            title: 'Error',
            message: error.message || 'Failed to update chart',
            type: 'error'
        });
      }
    },
    async exportChart() {
        if (!this.$refs.chart) return;
        
        const chartElement = this.$refs.chart;
        chartElement.classList.add('export-pure');
        
        // Temporary logic to handle zoom if handled by parent scale?
        // Parent passes NO zoom to this component effectively if it just scales the wrapper.
        // But if we want high res export...
        // HomePage logic: set zoomLevel = 1, then html2canvas.
        
        // Since this component is inside the wrapper which gets scaled,
        // we might rely on parent resetting zoom before calling this?
        // OR parent calls this, and this function doesn't care about zoomLevel of parent wrapper,
        // it just captures this.$refs.chart.
        // BUT if wrapper is scaled, html2canvas might capture scaled version.
        // HomePage `exportLayout` reset zoomLevel to 1 BEFORE calling html2canvas.
        
        // Use html2canvas
        // Note: html2canvas is imported.
        
        try {
            const canvas = await html2canvas(chartElement, {
              backgroundColor: this.backgroundColor,
              logging: false,
              scale: 2 // Better resolution? HomePage didn't set this explicitly but helpful.
            });
            
            chartElement.classList.remove('export-pure');
            
            const dataURL = canvas.toDataURL('image/png');
            const a = document.createElement('a');
            a.href = dataURL;
            const date = new Date();
            const dateString = `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`;
            const timestamp = Date.now();
            a.download = `wordcloudstudio_${dateString}_${timestamp}.png`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            
        } catch (e) {
            console.error(e);
            ElNotification({
                title: 'Export Failed',
                message: e.message,
                type: 'error'
            });
            chartElement.classList.remove('export-pure');
        }
    }
  }
}
</script>

<style scoped>
.export-pure {
  padding: 0 !important;
  margin: 0 !important;
  box-shadow: none !important;
  border-radius: 0 !important;
  background: #fff !important;
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;
  filter: none !important;
  border: none !important;
}

:deep(.el-carousel__arrow) {
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  transition: all 0.3s ease;
  color: #666 !important;
}

:deep(.el-carousel__arrow):hover {
  background: rgba(255, 255, 255, 0.95);
  color: #333 !important;
}
</style>
